import React, { Suspense, lazy } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Header from './components/Header'
import Footer from './components/Footer'
import StickyMobileBar from './components/StickyMobileBar'
import Home from './pages/Home'

const ServiceDetail = lazy(() => import('./pages/ServiceDetail'))
const Reviews = lazy(() => import('./pages/Reviews'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPostDetail = lazy(() => import('./pages/BlogPostDetail'))
const About = lazy(() => import('./pages/About'))
const Contact = lazy(() => import('./pages/Contact'))
const NotFound = lazy(() => import('./pages/NotFound'))

// ScrollToTop komponent for at sikre, at siden scroller til toppen ved ruteskift
function ScrollToTop() {
  const { pathname } = useLocation()

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

// AnalyticsTracker for Google Analytics 4 (Measurement ID: G-51H0QGEKD9)
// Registrerer korrekt sidetitel og sti ved alle ruteskift på tværs af hele hjemmesiden
function AnalyticsTracker() {
  const location = useLocation()

  React.useEffect(() => {
    // Giv et kort øjeblik så document.title og SEO meta opdateres først
    const timer = setTimeout(() => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_title: document.title,
          page_location: window.location.href,
          page_path: location.pathname + location.search
        })
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [location])

  return null
}

function AppContent() {
  return (
    <div className="relative min-h-screen bg-black text-white antialiased flex flex-col justify-between overflow-x-hidden">
      {/* Scroll to top on route change */}
      <ScrollToTop />

      {/* Google Analytics 4 route tracker */}
      <AnalyticsTracker />

      {/* Global Header */}
      <Header />

      {/* Main Content Area - with extra padding bottom on mobile to avoid overlap with StickyMobileBar */}
      <main className="flex-grow pb-16 md:pb-0">
        <Suspense fallback={<div className="min-h-[50vh] bg-black" />}>
          <Routes>
            {/* Forside */}
            <Route path="/" element={<Home />} />

            {/* Direkte ydelser URL'er som findes via Google og eksterne links */}
            <Route path="/dj-til-bryllup" element={<ServiceDetail defaultSlug="bryllup" />} />
            <Route path="/lej-dj-til-firmafest" element={<ServiceDetail defaultSlug="firmafest" />} />
            <Route path="/dj-til-firmafest" element={<ServiceDetail defaultSlug="firmafest" />} />
            <Route path="/dj-til-foedselsdag" element={<ServiceDetail defaultSlug="foedselsdag" />} />
            <Route path="/dj-og-saxofon" element={<ServiceDetail defaultSlug="dj-og-saxofon" />} />
            <Route path="/vinyl-dj" element={<ServiceDetail defaultSlug="vinyl-dj" />} />
            <Route path="/dj-ron9-fylder-dit-dansegulv/dj-til-fest" element={<ServiceDetail defaultSlug="dj-til-fest" />} />
            <Route path="/dj-til-fest" element={<ServiceDetail defaultSlug="dj-til-fest" />} />

            {/* Ydelser under /ydelser/ sti for fuld bagudkompatibilitet */}
            <Route path="/ydelser/:slug" element={<ServiceDetail />} />

            {/* Direkte blogindlæg URL'er som findes via Google og eksterne links */}
            <Route path="/hvad-koster-en-dj-til-bryllup" element={<BlogPostDetail defaultSlug="hvad-koster-en-dj-til-bryllup" />} />
            <Route path="/dj-til-bryllup-i-koebenhavn-saadan-vaelger-i" element={<BlogPostDetail defaultSlug="dj-til-bryllup-saadan-vaelger-i" />} />
            <Route path="/dj-til-bryllup-saadan-vaelger-i" element={<BlogPostDetail defaultSlug="dj-til-bryllup-saadan-vaelger-i" />} />
            <Route path="/dj-til-foedselsdag-der-fylder-dansegulvet" element={<BlogPostDetail defaultSlug="dj-til-foedselsdag-der-fylder-dansegulvet" />} />
            <Route path="/vinyl-dj-til-fest" element={<BlogPostDetail defaultSlug="vinyl-dj-til-fest" />} />
            <Route path="/vinyl-dj-til-fest-naar-musikken-skal-ses-og-maerkes" element={<BlogPostDetail defaultSlug="vinyl-dj-til-fest" />} />
            <Route path="/dj-til-firmafest-saadan-vaelger-du-rigtigt" element={<BlogPostDetail defaultSlug="dj-til-firmafest-saadan-vaelger-du-rigtigt" />} />
            <Route path="/vaelg-den-rigtige-dj-til-din-fest" element={<BlogPostDetail defaultSlug="saadan-vaelger-du-den-rigtige-dj-til-din-fest-i-koebenhavn-og-paa-sjaelland" />} />
            <Route path="/saadan-vaelger-du-den-rigtige-dj-til-din-fest-i-koebenhavn-og-paa-sjaelland" element={<BlogPostDetail defaultSlug="saadan-vaelger-du-den-rigtige-dj-til-din-fest-i-koebenhavn-og-paa-sjaelland" />} />
            <Route path="/hvorfor-en-professionel-dj-slaar-en-spotify-playliste-hver-gang" element={<BlogPostDetail defaultSlug="saadan-vaelger-du-den-rigtige-dj-til-din-fest-i-koebenhavn-og-paa-sjaelland" />} />

            {/* Standard sider */}
            <Route path="/anmeldelser" element={<Reviews />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />
            <Route path="/om" element={<About />} />
            <Route path="/kontakt" element={<Contact />} />

            {/* 404 Not Found - sender IKKE til forside, men viser en reel 404 fejl */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>

      {/* Global Footer */}
      <Footer />

      {/* Sticky Mobile CTA Bar */}
      <StickyMobileBar />
    </div>
  )
}

function App() {
  return (
    <AppContent />
  )
}

export default App
